import React, { useState, useMemo, useContext } from 'react';
import { mutate } from 'swr';
import { SectionTitle, Card, Badge } from '../../design-system';
import { getCustomerDisplayName, getCustomerTypeLabel, formatInteger } from '../../utils/formatters';
import { useGetCachedCaseStats } from '../../api/casestats';
import { useStartCaseInstance, useStopCaseInstance, usePauseCaseDiscovery, useResumeCaseDiscovery, useSetCheckImageAccess } from '../../api/admin';
import { useDeleteOrgCase } from '../../api/orgcases';
import { useConfirmAction } from './confirmactionhandler';
import ShowDeletedToggle from './ShowDeletedToggle';
import SnackbarContext from '../../context/snackbar-context.js';
import { handleDisplayResponseResults } from '../../api/api_util.js';
import styles from './AdminPanel.module.css';

const statusVariant = (s) => {
  switch ((s || '').toUpperCase()) {
    case 'ACTIVE': return 'success';
    case 'ERROR':
    case 'DELETED': return 'error';
    case 'CREATE_PENDING':
    case 'ARCHIVE_PENDING':
    case 'RESTORE_PENDING':
    case 'DELETE_PENDING': return 'warning';
    default: return 'default'; // ARCHIVED, unknown
  }
};

const statusLabel = (s) => {
  if (!s) return 'Unknown';
  return s.replace(/_/g, ' ').toLowerCase()
    .split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// Per-case check-image access tier (sales entitlement). Absent => NO_ACCESS.
const CHECK_IMAGE_ACCESS_LABELS = { NO_ACCESS: 'No Access', DEPOSITS: 'Deposits', FULL: 'Full' };
const checkImageAccessTier = (t) => {
  const up = (t || 'NO_ACCESS').toUpperCase();
  return CHECK_IMAGE_ACCESS_LABELS[up] ? up : 'NO_ACCESS';
};

// Statuses with no live, controllable case stack. The case DB is mid-create,
// being torn down, restoring from archive, or already archived — so Start/Stop
// DB and Discovery controls have nothing to act on (and the stats poll reads
// "DB offline" + "Discovery paused", which would otherwise light up Start DB
// and Resume Discovery). DELETED is handled separately by an early return.
// ACTIVE / ERROR (and an unknown status) keep their controls.
const NO_LIVE_DB_STATUSES = new Set([
  'CREATE_PENDING', 'ARCHIVE_PENDING', 'RESTORE_PENDING', 'DELETE_PENDING', 'ARCHIVED',
]);

function CaseRow({ caseData, onDelete }) {
  const startInstance = useStartCaseInstance();
  const stopInstance = useStopCaseInstance();
  const pauseDiscovery = usePauseCaseDiscovery();
  const resumeDiscovery = useResumeCaseDiscovery();
  const setCheckImageAccess = useSetCheckImageAccess();
  const { data: caseStats, isLoading } = useGetCachedCaseStats(caseData.caseID);

  // Check-image access tier — from the ORGCASES payload (primary DB), so it's
  // available even when the case DB is offline (unlike the caseStats badges).
  const checkImageTier = checkImageAccessTier(caseData.checkImageAccess);

  const status = (caseData.caseStatus || '').toUpperCase();
  const isDeleted = status === 'DELETED';
  const isDeletePending = status === 'DELETE_PENDING';
  // DB + Discovery controls only make sense for a case with a live, controllable
  // stack — suppress them for every create/teardown/restore/archived status.
  const showDbDiscoveryControls = !NO_LIVE_DB_STATUSES.has(status);

  // Tombstoned case: no live resources to monitor or control — show just the
  // name + DELETED badge.
  if (isDeleted) {
    return (
      <div className={styles.caseRow}>
        <div className={styles.caseName}>{caseData.caseName || caseData.caseID}</div>
        <div className={styles.chips}>
          <Badge variant={statusVariant(caseData.caseStatus)}>{statusLabel(caseData.caseStatus)}</Badge>
        </div>
        <div className={styles.actions} />
      </div>
    );
  }

  const stats = caseStats?.stats || {};
  const instanceState = (caseStats?.db_state?.instance_state || '').toLowerCase();
  const dbOnline = String(caseStats?.db_state?.ping_response?.status_code).includes('200');
  const isRunning = instanceState === 'running';
  // EC2 transitional states — the DB is mid-start or mid-stop, so neither a
  // Start nor a Stop action is valid; the status badge shows the live state.
  const isDbTransitioning = ['pending', 'stopping', 'shutting-down', 'rebooting'].includes(instanceState);
  // The DB is up (or coming up) when it pings 200 or reports running.
  const canStopDb = dbOnline || isRunning;
  // Offer Start for any state that is not up and not transitioning: stopped,
  // an empty/unresolved "offline" poll, or an unknown/terminated state. The
  // per-case Neo4j EC2 is idle-reaped to "stopped", and a poll can also land
  // on an empty instance_state — neither should leave the row un-actionable.
  const canStartDb = !canStopDb && !isDbTransitioning;
  // Discovery is opt-in (paused by default). reasons.paused !== false means
  // paused — matches the backend coalesce(default true) so a case with no
  // explicit flag reads as paused.
  const discoveryPaused = caseStats?.discovery_status?.reasons?.paused !== false;

  return (
    <div className={styles.caseRow}>
      <div className={styles.caseName}>{caseData.caseName || caseData.caseID}</div>

      <div className={styles.chips}>
        {isLoading || !caseStats ? (
          <span className={styles.muted}>loading stats…</span>
        ) : (
          <>
            <Badge variant="default">
              {formatInteger(stats.quota_pages_used ?? 0)}/{formatInteger(stats.quota_pages_total ?? 0)} pages
            </Badge>
            <Badge variant={stats.bad_scan_count > 0 ? 'warning' : 'default'}>
              {formatInteger(stats.bad_scan_count ?? 0)}/{formatInteger(stats.total_scan_count ?? 0)} bad scans
            </Badge>
            <Badge variant={stats.unenriched_transaction_count > 0 ? 'warning' : 'default'}>
              {formatInteger(stats.unenriched_transaction_count ?? 0)}/{formatInteger(stats.transaction_count ?? 0)} unenriched
            </Badge>
            <Badge variant={dbOnline ? 'success' : 'error'}>
              {dbOnline ? 'DB online' : `DB ${instanceState || 'offline'}`}
            </Badge>
            <Badge variant={discoveryPaused ? 'default' : 'accent'}>
              Discovery: {discoveryPaused ? 'Paused' : 'Active'}
            </Badge>
          </>
        )}
        {/* Entitlement badge — reads from caseData (primary DB), so it renders
            regardless of the caseStats poll / whether the case DB is online. */}
        <Badge variant={checkImageTier === 'NO_ACCESS' ? 'default' : 'accent'}>
          Check Images: {CHECK_IMAGE_ACCESS_LABELS[checkImageTier]}
        </Badge>
        <Badge variant={statusVariant(caseData.caseStatus)}>{statusLabel(caseData.caseStatus)}</Badge>
      </div>

      <div className={styles.actions}>
        {/* DB + Discovery controls are meaningless unless the case has a live
            case DB to act on — see NO_LIVE_DB_STATUSES. */}
        {showDbDiscoveryControls && canStartDb && (
          <button type="button" className={styles.startBtn}
                  onClick={() => startInstance(caseData.caseID)}
                  aria-label={`Start database for ${caseData.caseName || caseData.caseID}`}>
            Start DB
          </button>
        )}
        {showDbDiscoveryControls && canStopDb && (
          <button type="button" className={styles.stopBtn}
                  onClick={() => stopInstance(caseData.caseID)}
                  aria-label={`Stop database for ${caseData.caseName || caseData.caseID}`}>
            Stop DB
          </button>
        )}
        {showDbDiscoveryControls && caseStats && (
          discoveryPaused ? (
            <button type="button" className={styles.resumeBtn}
                    onClick={() => resumeDiscovery(caseData.caseID, caseStats)}
                    aria-label={`Resume discovery for ${caseData.caseName || caseData.caseID}`}>
              Resume Discovery
            </button>
          ) : (
            <button type="button" className={styles.pauseBtn}
                    onClick={() => pauseDiscovery(caseData.caseID, caseStats)}
                    aria-label={`Pause discovery for ${caseData.caseName || caseData.caseID}`}>
              Pause Discovery
            </button>
          )
        )}
        {/* Check-image access tier (sales entitlement). Primary-DB backed, so
            it's settable even when the case DB is offline — no live-DB gate. */}
        {!isDeletePending && (
          <label className={styles.tierControl}>
            <span className={styles.tierLabel}>Check images</span>
            <select
              className={styles.tierSelect}
              value={checkImageTier}
              onChange={(e) => setCheckImageAccess(caseData.caseID, e.target.value)}
              aria-label={`Check-image access for ${caseData.caseName || caseData.caseID}`}
            >
              <option value="NO_ACCESS">No Access</option>
              <option value="DEPOSITS">Deposits</option>
              <option value="FULL">Full</option>
            </select>
          </label>
        )}
        {onDelete && !isDeletePending && (
          <button type="button" className={styles.deleteBtn}
                  onClick={() => onDelete(caseData)}
                  aria-label={`Permanently delete ${caseData.caseName || caseData.caseID}`}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function CustomerGroup({ group, onDelete }) {
  const [open, setOpen] = useState(true);
  return (
    <Card padding="none" className={styles.group}>
      <button type="button" className={styles.groupHeader} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className={styles.chevron} aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className={styles.groupName}>{group.customerName}</span>
        {group.customerType && <Badge variant="info">{group.customerType}</Badge>}
        <span className={styles.groupCount}>
          {group.cases.length} case{group.cases.length === 1 ? '' : 's'}
        </span>
      </button>
      {open && (
        <div className={styles.groupBody}>
          {group.cases.map((c) => <CaseRow key={c.caseID} caseData={c} onDelete={onDelete} />)}
        </div>
      )}
    </Card>
  );
}

export default function AdminPanel({ customerCases, isLoading, error }) {
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const snackbarContext = useContext(SnackbarContext);
  const deleteOrgCase = useDeleteOrgCase();

  const handleDelete = async (caseData) => {
    // Optimistic update — immediately show DELETE_PENDING in the admin list.
    mutate(
      ['ORGCASES'],
      (cur) => {
        if (!Array.isArray(cur)) return cur;
        return cur.map((it) =>
          it.case?.caseID === caseData.caseID
            ? { ...it, case: { ...it.case, status: 'DELETE_PENDING' } }
            : it,
        );
      },
      { revalidate: false },
    );
    try {
      const response = await deleteOrgCase(caseData.customerID, caseData.caseID);
      if (snackbarContext) {
        handleDisplayResponseResults(
          response,
          snackbarContext,
          () => `Initiated permanent deletion of case "${caseData.caseName || caseData.caseID}"`,
          () => `Case "${caseData.caseName || caseData.caseID}" could not be deleted`,
        );
      }
      mutate(['ORGCASES']);
    } catch (err) {
      console.error('Error deleting case:', err);
      if (snackbarContext) snackbarContext.openSnackbarError('Failed to delete case');
      mutate(['ORGCASES']);
    }
  };

  const { handleConfirmAction: handleConfirmDelete, ConfirmDialogElement: ConfirmDeleteDialog } =
    useConfirmAction(handleDelete, {
      title: 'Permanently Delete Case',
      entityNamePrefix: 'You are about to permanently delete the following case:',
      getEntityName: (c) => c?.caseName || c?.caseID,
      confirmMessage:
        'This permanently destroys ALL data for this case (documents, analysis, and case database) and cannot be undone. '
        + 'Any outstanding charges will be finalized. Type "delete" to confirm.',
      confirmKeyword: 'delete',
      proceedButtonLabel: 'Delete Permanently',
    });

  const groups = useMemo(() => {
    const rows = (customerCases || []).map((item) => ({
      customerID: item.customer?.customerID || 'unassigned',
      customerName: item.customer ? getCustomerDisplayName(item.customer) : 'Unassigned',
      customerType: item.customer ? getCustomerTypeLabel(item.customer) : null,
      caseID: item.case?.caseID,
      caseName: item.case?.caseName,
      caseStatus: item.case?.status,
      // Sales entitlement (primary-DB :Case prop); absent => NO_ACCESS. Rides
      // in the ORGCASES payload, so it's available with no case-EC2 dependency.
      checkImageAccess: item.case?.checkImageAccess,
    }));
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      // Hide tombstoned cases unless the toggle is on (they remain in the data
      // for billing/cost reconciliation).
      if ((r.caseStatus || '').toUpperCase() === 'DELETED' && !showDeleted) return false;
      if (!q) return true;
      return (r.customerName || '').toLowerCase().includes(q) || (r.caseName || '').toLowerCase().includes(q);
    });
    const byCustomer = {};
    filtered.forEach((r) => {
      const g = byCustomer[r.customerID] || (byCustomer[r.customerID] = {
        customerID: r.customerID, customerName: r.customerName, customerType: r.customerType, cases: [],
      });
      g.cases.push(r);
    });
    return Object.values(byCustomer).sort((a, b) => (a.customerName || '').localeCompare(b.customerName || ''));
  }, [customerCases, search, showDeleted]);

  return (
    <>
      <section className={styles.section}>
        <div className={styles.toolbar}>
          <input
            type="text"
            className={styles.search}
            placeholder="Filter by customer or case name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Filter cases"
          />
          <ShowDeletedToggle checked={showDeleted} onChange={setShowDeleted} />
        </div>
      </section>

      {isLoading && <Card padding="md"><p className={styles.muted}>Loading cases…</p></Card>}
      {error && <Card padding="md"><p className={styles.muted}>Could not load cases.</p></Card>}
      {!isLoading && !error && groups.length === 0 && (
        <Card padding="md"><p className={styles.muted}>No cases found.</p></Card>
      )}

      {groups.length > 0 && (
        <section className={styles.section}>
          <SectionTitle>Cases by customer</SectionTitle>
          <div className={styles.groups}>
            {groups.map((g) => <CustomerGroup key={g.customerID} group={g} onDelete={handleConfirmDelete} />)}
          </div>
        </section>
      )}

      {/* Permanent Delete Confirmation Dialog (type "delete" to confirm) */}
      <ConfirmDeleteDialog />
    </>
  );
}
